import React, { useEffect, useState } from 'react'
import { Box, Button, FormField, Text, TextInput } from 'grommet'
import { I18n, GlobalConfig, Constants } from 'galarm-config'
import { TaskManager } from 'galarm-shared'
import GalarmWebHeader from './GalarmWebHeader'
import { getAppPremiumProducts } from '../utils/getAppPremiumProducts'
import { PlanCards, FeatureList } from './PremiumComponents'

const SUCCESS_URL = `${window.location.origin}/premium/success`
const CANCEL_URL = `${window.location.origin}/premium/cancel`

const BuyPremiumScreen = ({
  uid,
  initialInterval,
  initialEmail,
  initialName,
  initialPhoneNumber,
  initialCountryCode
}) => {
  const [selectedInterval, setSelectedInterval] = useState(
    initialInterval || 'year'
  )
  const [customerId, setCustomerId] = useState(null)
  const [billingEmail, setBillingEmail] = useState(initialEmail || '')
  const [product, setProduct] = useState(null)
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true)
  const [stripeSubscriptions, setStripeSubscriptions] = useState([])
  const [error, setError] = useState('')

  useEffect(() => {
    getAppPremiumProducts()
      .then(result => {
        const products = result.products || []
        const firstProduct = products[0] || null

        if (!firstProduct || !Array.isArray(firstProduct.prices)) {
          setError(I18n.t('problemWithCreatingCheckoutSession'))
          setLoadingProducts(false)
          return
        }

        setProduct(firstProduct)

        const hasYearlyPrice = firstProduct.prices.some(
          price => price.recurring?.interval === 'year'
        )
        if (!hasYearlyPrice) {
          const firstInterval =
            firstProduct.prices[0]?.recurring?.interval || 'one_time'
          setSelectedInterval(firstInterval)
        }

        setLoadingProducts(false)
      })
      .catch(() => {
        setError(I18n.t('unableToLoadPremiumPlans'))
        setLoadingProducts(false)
      })
  }, [])

  useEffect(() => {
    if (!uid) {
      setLoadingSubscriptions(false)
      return
    }

    GlobalConfig.firebaseHelpers
      .getData(
        GlobalConfig.firebaseHelpers.createRef(
          `userInfos/${uid}/stripeSubscriptions`
        )
      )
      .then(snapshot => {
        const subscriptions = []
        const nowInSeconds = Date.now() / 1000
        if (snapshot.exists()) {
          snapshot.forEach(subSnapshot => {
            const sub = subSnapshot.val()
            const isLifetimePlan = sub.interval === 'lifetime'
            if (
              (sub.status === 'active' ||
                sub.status === 'trialing' ||
                sub.status === 'past_due') &&
              (isLifetimePlan || sub.subscriptionEnd > nowInSeconds)
            ) {
              subscriptions.push(sub)
            }
          })
        }
        setStripeSubscriptions(subscriptions)
      })
      .catch(() => {
        setStripeSubscriptions([])
      })
      .finally(() => {
        setLoadingSubscriptions(false)
      })
  }, [uid])

  const monthlyPrice = product?.prices?.find(
    p => p.recurring?.interval === 'month'
  )
  const yearlyPrice = product?.prices?.find(
    p => p.recurring?.interval === 'year'
  )
  const lifetimePrice = product?.prices?.find(p => p.type === 'one_time')
  const selectedPrice = product?.prices?.find(
    p =>
      (p.type === 'one_time' && selectedInterval === 'one_time') ||
      p.recurring?.interval === selectedInterval
  )

  const activeStripeSubscription = stripeSubscriptions[0]
  const hasActiveStripeSubscription = !!activeStripeSubscription

  const stripePlanName =
    activeStripeSubscription?.interval === 'month'
      ? I18n.t('monthlySubscription')
      : activeStripeSubscription?.interval === 'year'
      ? I18n.t('yearlySubscription')
      : I18n.t('lifetimePremium')

  const onOpenBillingPortal = () => {
    const activeCustomerId = activeStripeSubscription?.customerId
    if (!activeCustomerId) {
      setError(I18n.t('problemWithCreatingCustomerPortalSession'))
      return
    }

    setError('')
    GlobalConfig.showProgress({
      state: Constants.ProgressStates.IN_PROGRESS,
      message: I18n.t('openingBillingPortal'),
      closeable: false
    })

    TaskManager.addHttpsCloudTask('stripeCreateAppCustomerPortalSession', {
      customerId: activeCustomerId,
      returnUrl: `${window.location.origin}/premium`
    })
      .then(result => {
        const url = result.session?.url
        if (!url) {
          setError(I18n.t('problemWithCreatingCustomerPortalSession'))
          GlobalConfig.dismissProgress()
          return
        }
        window.location.href = url
      })
      .catch(() => {
        GlobalConfig.dismissProgress()
        setError(I18n.t('problemWithCreatingCustomerPortalSession'))
      })
  }

  const onBuyNow = () => {
    if (!product || !selectedPrice) {
      setError(I18n.t('noPlanSelected'))
      return
    }

    const email = billingEmail.trim()
    const customerName = (initialName || '').trim()
    const phoneNumber = `${initialCountryCode || ''}${
      initialPhoneNumber || ''
    }`.trim()
    if (!email) {
      setError(I18n.t('specifyEmailId'))
      return
    }
    // Basic email format validation before creating checkout session.
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError(I18n.t('specifyValidEmailId'))
      return
    }

    setError('')
    GlobalConfig.showProgress({
      state: Constants.ProgressStates.IN_PROGRESS,
      message: I18n.t('creatingCheckoutSession'),
      closeable: false
    })

    const customerPromise = customerId
      ? Promise.resolve(customerId)
      : TaskManager.addHttpsCloudTask('stripeCreateAppCustomer', {
          uid,
          name: customerName,
          email,
          phoneNumber
        }).then(result => {
          const id = result.customerId

          if (!id) {
            throw new Error('Problem creating Stripe customer')
          }

          setCustomerId(id)
          return id
        })

    customerPromise
      .then(currentCustomerId =>
        TaskManager.addHttpsCloudTask('stripeCreateAppCheckoutSession', {
          customerId: currentCustomerId,
          customerName,
          phoneNumber,
          productId: product.id,
          priceId: selectedPrice.id,
          interval: selectedInterval,
          successUrl: SUCCESS_URL,
          cancelUrl: CANCEL_URL
        })
      )
      .then(result => {
        const url = result.session?.url
        if (!url) {
          GlobalConfig.dismissProgress()
          setError(I18n.t('problemWithCreatingCheckoutSession'))
          return
        }
        window.location.href = url
      })
      .catch(err => {
        GlobalConfig.dismissProgress()
        setError(
          I18n.t('problemWithCreatingCheckoutSession', {
            error: err.error || err.message || ''
          })
        )
      })
  }

  if (
    (loadingProducts || loadingSubscriptions) &&
    !hasActiveStripeSubscription
  ) {
    return (
      <Box fill>
        <GalarmWebHeader />
        <Box flex align="center" justify="center">
          <Text color="dark-4">{I18n.t('loadingProducts')}</Text>
        </Box>
      </Box>
    )
  }

  return (
    <Box fill background="#f9f9fb">
      <GalarmWebHeader />

      {error && (
        <Box
          pad={{ horizontal: 'large', vertical: 'xsmall' }}
          background="status-error">
          <Text color="white" size="small" textAlign="center">
            {error}
          </Text>
        </Box>
      )}

      <Box flex overflow="auto">
        <Box align="center" pad="large" flex={{ shrink: 0 }}>
          <Box width="100%" gap="large" style={{ maxWidth: '960px' }}>
            {/* Hero */}
            <Box align="center">
              <Text
                size="xxlarge"
                weight="bold"
                color="dark-1"
                style={{ letterSpacing: '-0.5px', marginTop: '8px' }}>
                {I18n.t('galarmPremium')}
              </Text>
            </Box>

            {/* Plan cards */}
            {hasActiveStripeSubscription ? (
              <Box
                background="white"
                round="small"
                pad={{ horizontal: 'medium', vertical: 'large' }}
                gap="small"
                align="center"
                style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.07)' }}>
                <Text weight="bold" textAlign="center">
                  {I18n.t('currentStripePremiumSubscriptionStatus', {
                    planName: stripePlanName
                  })}
                </Text>
                <Text size="small" color="dark-4" textAlign="center">
                  {I18n.t('manageSubscriptionFromBillingPortal')}
                </Text>
                <Button
                  primary
                  label={
                    <Text color="white">{I18n.t('openBillingPortal')}</Text>
                  }
                  onClick={onOpenBillingPortal}
                  style={{ borderRadius: '8px', fontWeight: 600 }}
                />
              </Box>
            ) : (
              <PlanCards
                monthlyPrice={monthlyPrice}
                yearlyPrice={yearlyPrice}
                lifetimePrice={lifetimePrice}
                selectedInterval={selectedInterval}
                onIntervalChange={setSelectedInterval}
              />
            )}

            {/* Purchase form */}
            {!hasActiveStripeSubscription && (
              <Box
                background="white"
                round="small"
                pad="medium"
                gap="small"
                style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.07)' }}>
                <FormField
                  label={
                    <Text size="small" weight="bold" color="dark-2">
                      {I18n.t('emailId')}
                    </Text>
                  }
                  margin="none">
                  <TextInput
                    type="email"
                    value={billingEmail}
                    onChange={event => setBillingEmail(event.target.value)}
                    placeholder="name@example.com"
                  />
                </FormField>
                <Text size="small" color="dark-4">
                  {I18n.t('invoicesSentToThisEmail')}
                </Text>

                <Button
                  primary
                  size="large"
                  alignSelf="center"
                  label={<Text color="white">{I18n.t('checkout')}</Text>}
                  onClick={onBuyNow}
                  disabled={!selectedPrice}
                  style={{
                    marginTop: '8px',
                    borderRadius: '8px',
                    fontWeight: 600
                  }}
                />
              </Box>
            )}

            {/* Features */}
            <FeatureList />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default BuyPremiumScreen
